import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock,
  Server, Database, Wifi, Shield, ArrowUpRight, Copy, ExternalLink,
  ChevronDown, ChevronUp, Zap, Globe, Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Helpers ──────────────────────────────────────────────────────────── */

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' · ' +
    d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function latencyColor(ms) {
  if (ms < 50) return 'text-emerald-600';
  if (ms < 200) return 'text-amber-600';
  return 'text-red-600';
}

/* ── Status Indicator ─────────────────────────────────────────────────── */

function StatusPulse({ status }) {
  const isHealthy = status === 'healthy' || status === 'running' || status === 'connected';
  return (
    <span className="relative flex h-3 w-3" data-testid="status-pulse">
      {isHealthy && (
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
      )}
      <span className={cn(
        "relative inline-flex rounded-full h-3 w-3",
        isHealthy ? "bg-emerald-500" : "bg-red-500"
      )} />
    </span>
  );
}

/* ── Uptime Bar ───────────────────────────────────────────────────────── */

function UptimeBar({ checks }) {
  if (!checks || checks.length === 0) return null;
  // Show last 60 checks as tiny bars
  const display = checks.slice(0, 60).reverse();
  return (
    <div className="flex gap-[2px] items-end h-8" data-testid="uptime-bar">
      {display.map((c, i) => (
        <div
          key={i}
          className={cn(
            "flex-1 min-w-[3px] max-w-[6px] rounded-sm transition-all",
            c.status === 'healthy' ? 'bg-emerald-400 hover:bg-emerald-500' : 'bg-red-400 hover:bg-red-500'
          )}
          style={{ height: `${Math.min(100, Math.max(20, (c.db_latency_ms || 1) * 2))}%` }}
          title={`${c.status} · ${c.db_latency_ms}ms · ${formatTime(c.timestamp)}`}
        />
      ))}
    </div>
  );
}

/* ── Response Time Mini Chart ─────────────────────────────────────────── */

function ResponseChart({ checks }) {
  if (!checks || checks.length < 2) return <p className="text-xs text-gray-400">Collecting data...</p>;
  const data = checks.slice(0, 60).reverse();
  const maxMs = Math.max(...data.map(c => c.db_latency_ms || 0), 5);
  const h = 48;
  const w = 240;
  const points = data.map((c, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((c.db_latency_ms || 0) / maxMs) * (h - 4);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12" data-testid="response-chart">
      <polyline
        points={points}
        fill="none"
        stroke="rgb(16, 185, 129)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {data.map((c, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = h - ((c.db_latency_ms || 0) / maxMs) * (h - 4);
        return c.status !== 'healthy' ? (
          <circle key={i} cx={x} cy={y} r="3" fill="rgb(239, 68, 68)" />
        ) : null;
      })}
    </svg>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────── */

export default function SystemHealthPage() {
  const [status, setStatus] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [history, setHistory] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, uRes, hRes, iRes] = await Promise.allSettled([
        axios.get(`${API}/api/system-health/status`),
        axios.get(`${API}/api/system-health/uptime`),
        axios.get(`${API}/api/system-health/history?hours=24`),
        axios.get(`${API}/api/system-health/incidents`),
      ]);
      if (sRes.status === 'fulfilled') setStatus(sRes.value.data);
      if (uRes.status === 'fulfilled') setUptime(uRes.value.data);
      if (hRes.status === 'fulfilled') setHistory(hRes.value.data);
      if (iRes.status === 'fulfilled') setIncidents(iRes.value.data);
    } catch (e) {
      toast.error('Failed to load health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 60s
  useEffect(() => {
    const iv = setInterval(fetchAll, 60000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const serverUp = status?.server?.status === 'running';
  const dbUp = status?.database?.status === 'connected';
  const allHealthy = serverUp && dbUp;

  const copyPingUrl = () => {
    const url = `${window.location.origin}/api/system-health/ping`;
    navigator.clipboard.writeText(url);
    toast.success('Health check URL copied!');
  };

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="system-health-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">System Health</h1>
            {status && (
              <Badge
                data-testid="overall-status-badge"
                className={cn(
                  "gap-1.5 text-xs",
                  allHealthy
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-red-50 text-red-700 border-red-200"
                )}
              >
                <StatusPulse status={allHealthy ? 'healthy' : 'degraded'} />
                {allHealthy ? 'All Systems Operational' : 'Issues Detected'}
              </Badge>
            )}
          </div>
          <p className="text-gray-medium text-sm mt-1">
            Monitor server uptime, database health, and response times
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-health-btn">
          <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Status Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Server Status */}
        <Card className="bg-white border-gray-border" data-testid="server-status-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-blue-50"><Server className="h-4 w-4 text-blue-600" /></div>
              <StatusPulse status={status?.server?.status || 'unknown'} />
            </div>
            <p className="text-xs text-gray-medium mb-0.5">Backend Server</p>
            <p className="text-lg font-semibold text-text-primary capitalize">{status?.server?.status || '...'}</p>
            {status?.server?.uptime_human && (
              <p className="text-xs text-gray-400 mt-1">Uptime: {status.server.uptime_human}</p>
            )}
          </CardContent>
        </Card>

        {/* Database Status */}
        <Card className="bg-white border-gray-border" data-testid="db-status-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-violet-50"><Database className="h-4 w-4 text-violet-600" /></div>
              <StatusPulse status={status?.database?.status || 'unknown'} />
            </div>
            <p className="text-xs text-gray-medium mb-0.5">Database</p>
            <p className="text-lg font-semibold text-text-primary capitalize">{status?.database?.status || '...'}</p>
            {status?.database?.latency_ms != null && (
              <p className={cn("text-xs mt-1", latencyColor(status.database.latency_ms))}>
                {status.database.latency_ms}ms latency
              </p>
            )}
          </CardContent>
        </Card>

        {/* Uptime 24h */}
        <Card className="bg-white border-gray-border" data-testid="uptime-24h-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-emerald-50"><ArrowUpRight className="h-4 w-4 text-emerald-600" /></div>
              <Zap className="h-4 w-4 text-gray-300" />
            </div>
            <p className="text-xs text-gray-medium mb-0.5">Uptime (24h)</p>
            <p className="text-lg font-semibold text-text-primary">
              {uptime?.['24h']?.uptime_pct != null ? `${uptime['24h'].uptime_pct}%` : '—'}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {uptime?.['24h']?.total_checks || 0} checks logged
            </p>
          </CardContent>
        </Card>

        {/* Uptime 7d */}
        <Card className="bg-white border-gray-border" data-testid="uptime-7d-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-amber-50"><Shield className="h-4 w-4 text-amber-600" /></div>
              <Activity className="h-4 w-4 text-gray-300" />
            </div>
            <p className="text-xs text-gray-medium mb-0.5">Uptime (7 days)</p>
            <p className="text-lg font-semibold text-text-primary">
              {uptime?.['7d']?.uptime_pct != null ? `${uptime['7d'].uptime_pct}%` : '—'}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {uptime?.['7d']?.total_checks || 0} checks logged
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Uptime Timeline + Response Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-gray-border" data-testid="uptime-timeline-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Wifi className="h-4 w-4 text-emerald-500" />
              Uptime Timeline (Last 60 checks)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <UptimeBar checks={history} />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>Older</span>
              <span>Recent</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-border" data-testid="response-time-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-500" />
              DB Response Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponseChart checks={history} />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>Older</span>
              <span>
                Avg: {history.length > 0
                  ? (history.reduce((s, c) => s + (c.db_latency_ms || 0), 0) / history.length).toFixed(1)
                  : '—'
                }ms
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Info */}
      <Card className="bg-white border-gray-border" data-testid="system-info-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Server className="h-4 w-4 text-gray-500" />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Version</p>
              <p className="font-medium">{status?.server?.version || '—'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Started At</p>
              <p className="font-medium">{status?.server?.started_at ? formatTime(status.server.started_at) : '—'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Collections</p>
              <p className="font-medium">{status?.database?.collections ?? '—'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Total Users</p>
              <p className="font-medium">{status?.database?.users ?? '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Incidents */}
      <Card className="bg-white border-gray-border" data-testid="incidents-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Recent Incidents
          </CardTitle>
        </CardHeader>
        <CardContent>
          {incidents.length === 0 ? (
            <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              No incidents recorded. All clear!
            </div>
          ) : (
            <div className="space-y-2">
              {incidents.map((inc, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0 text-sm">
                  <div className={cn(
                    "w-2 h-2 rounded-full flex-shrink-0",
                    inc.ended_at ? "bg-emerald-400" : "bg-red-500 animate-pulse"
                  )} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate">{inc.message}</p>
                    <p className="text-xs text-gray-400">
                      {formatTime(inc.started_at)}
                      {inc.ended_at ? ` — Resolved in ${Math.round((inc.duration_seconds || 0) / 60)}m` : ' — Ongoing'}
                    </p>
                  </div>
                  <Badge variant={inc.ended_at ? 'outline' : 'destructive'} className="text-[10px] flex-shrink-0">
                    {inc.ended_at ? 'Resolved' : 'Active'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* External Monitoring Setup Guide */}
      <Card className="bg-white border-gray-border" data-testid="monitoring-setup-card">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowSetup(!showSetup)}>
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-blue-500" />
              External Monitoring Setup (UptimeRobot)
            </span>
            {showSetup ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </CardTitle>
        </CardHeader>
        {showSetup && (
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500">
              Connect an external monitor to get alerted even when you're not logged in.
              UptimeRobot's free tier checks your site every 5 minutes.
            </p>

            {/* Health Check URL */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-1.5">Health Check URL</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white border rounded px-2 py-1.5 flex-1 truncate font-mono" data-testid="ping-url">
                  {window.location.origin}/api/system-health/ping
                </code>
                <Button size="sm" variant="outline" onClick={copyPingUrl} data-testid="copy-ping-url-btn">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Steps */}
            <div className="space-y-2.5">
              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Quick Setup</p>
              {[
                { step: 1, text: 'Go to uptimerobot.com and create a free account' },
                { step: 2, text: 'Click "Add New Monitor" → select "HTTP(s)"' },
                { step: 3, text: 'Paste the health check URL above' },
                { step: 4, text: 'Set monitoring interval to 5 minutes' },
                { step: 5, text: 'Add your email/phone for alert notifications' },
              ].map(({ step, text }) => (
                <div key={step} className="flex gap-3 items-start text-sm">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex items-center justify-center">
                    {step}
                  </span>
                  <span className="text-gray-600">{text}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" asChild>
                <a href="https://uptimerobot.com" target="_blank" rel="noopener noreferrer" data-testid="uptimerobot-link">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  Open UptimeRobot
                </a>
              </Button>
            </div>

            {/* Push Alerts Note */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 flex gap-2.5">
              <Bell className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700">
                <p className="font-medium">Built-in alerts are active</p>
                <p className="mt-0.5 text-blue-600">
                  Konnect automatically sends push notifications to all admins when downtime is detected.
                  The background monitor checks every 60 seconds.
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
